import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {PublicationContext} from 'centrifuge';
import {camelizeKeys} from 'humps';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {CalendarPanelType} from '../../page/main/posts/calendar/posts-calendar-type';
import {PostStepEnum} from '../../page/main/posts/post-form/post-form-type';
import {
    PostsFilterQueriesType,
    PostsFilterStateUrlQueryNameEnum,
} from '../../page/main/posts/posts-filter/posts-filter-type';
import {ShortCatalogType} from '../../provider/catalogs/catalogs-type';
import {useCentrifugeSubscription} from '../../provider/centrifuge/centrifuge-hook';
import {Locale} from '../../provider/locale/localization';
import {MainFilterContext} from '../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../provider/main-filter/main-filter-type';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {useUser} from '../../provider/user/user-hook';
import {FormInstance} from '../../typings/antd';
import {isArraysEqual} from '../../util/array';
import {useCursorPagination} from '../../util/cursor-pagination/cursor-pagination-hook';
import {getIsoYyyyMmDdString} from '../../util/date';
import {getEnumValue, getEnumValueEnsure} from '../../util/enum';
import {ApiError, FormErrorType, getFormErrors} from '../../util/error';
import {HttpCodeEnum} from '../../util/fetch-const';
import {noop} from '../../util/function';
import {useRefreshId} from '../../util/hook';
import {usePagination} from '../../util/pagination-hook/pagination-hook';
import {IdNumberType, ProvidersIdsEnum} from '../../util/type';
import {useUrl} from '../../util/url-hook/url-hook';
import {useApiHooks} from '../api-hook/api-hook';
import {UseHookType} from '../api-hook/api-hook-type';

import {
    createPostApi,
    fetchAvailableFbPages,
    fetchAvailableInstagramPages,
    fetchAvailableVkGroups,
    fetchCompanyPreviewData,
    getLastPostsApi,
    getPostApi,
    getPostAvailableCatalogsApi,
    getPostCatalogsApi,
    getPostGenerationOptions,
    getPostLinks,
    getPostsCalendar,
    getPostsListApi,
    getPostSubmitInformation,
    hardRemovePostApi,
    refreshPost,
    removePostApi,
    requestPostImageSuggest,
    requestPostSuggest,
    updatePostApi,
} from './posts-api';
import {
    FORM_STEP_FIELDS,
    getCentrifugePostSuggestChannel,
    getInitialPostsFilter,
    isAiSuggestImageAction,
    isAiSuggestLegacyAction,
    isAiSuggestTextAction,
    parseTextActionSuggest,
} from './posts-helper';
import {aiGenerationErrorTranslationMap} from './posts-hook-const';
import {
    AiGenerationErrorEnum,
    AiImageSizeEnum,
    CalendarPostsRequestParametersType,
    CalendarPostsResponseType,
    CatalogLinksHookType,
    FetchPostType,
    LastPostsHookType,
    LastPostsType,
    PostAiGeneratedImageType,
    PostAiGenerationSettingsType,
    PostAiSuggestHookType,
    PostAiSuggestImageActionType,
    PostAiSuggestItemType,
    PostAiSuggestTextActionType,
    PostFbPageType,
    PostFormFieldsEnum,
    PostFormType,
    PostGenerationOptionsType,
    PostInstagramPageType,
    PostLinksFilterType,
    PostListItemType,
    PostPreviewDetailsType,
    PostPreviewOptionsType,
    PostsAvailableCatalogsHookType,
    PostsFilterKeyEnum,
    PostsFilterStateType,
    PostsHookPropsType,
    PostsHookType,
    PostSourceEnum,
    PostStatusEnum,
    PostSubmitInformationParametersType,
    PostSubmitInformationType,
    PostVkGroupType,
    SetPostFilterType,
    SourcePostFormFieldsEnum,
    UsePostFilterHookType,
} from './posts-types';

// use react query
export function usePosts(props: PostsHookPropsType): PostsHookType {
    const {filter, mainFilterKey, enabled = true} = props;
    const {isInProgress, setIsInProgress, result, setResult, processError, setProcessError, reset} =
        useApiHooks<Array<PostListItemType>>();
    const {refreshId, refresh: refreshPostsList} = useRefreshId();
    const [count, setCount] = useState<number | null>(null);

    // Signature of the last request; lets us reuse loaded posts across tab switches and only refetch on real changes.
    const lastRequestKeyRef = useRef<string | null>(null);
    // Set when an explicit refresh is requested (e.g. after a post action) so we refetch even if the signature is unchanged.
    const isForcedRefreshRef = useRef<boolean>(false);

    const refresh = useCallback(() => {
        isForcedRefreshRef.current = true;
        refreshPostsList();
    }, [refreshPostsList]);

    const paginationDependencies = useMemo(() => {
        return {
            mainFilterKey,
            ...filter,
            refreshId,
        };
    }, [filter, mainFilterKey, refreshId]);

    const cursorPagination = useCursorPagination({
        dependencies: paginationDependencies,
    });

    const {pageSize, cursor, onDataLoaded, onDataLoadFailed, refreshId: paginationRefreshId} = cursorPagination;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        // Reuse loaded posts when nothing meaningful changed (e.g. returning to the Posts tab); only the filter,
        // pagination, main filter, or an explicit refresh should trigger a new request.
        const requestKey = JSON.stringify({filter, mainFilterKey, cursor, pageSize});

        if (!isForcedRefreshRef.current && lastRequestKeyRef.current === requestKey) {
            return;
        }

        isForcedRefreshRef.current = false;
        lastRequestKeyRef.current = requestKey;

        setIsInProgress(true);
        setProcessError(null);

        getPostsListApi(filter, {pageSize, cursor}, mainFilterKey)
            .then((postsResponse) => {
                setResult(postsResponse.results);
                setCount(postsResponse.count ?? null);
                onDataLoaded(postsResponse);
                setProcessError(null);
            })
            .finally(() => {
                setIsInProgress(false);
            })
            .catch((error: Error) => {
                // Allow a retry on the next trigger after a failed request.
                lastRequestKeyRef.current = null;
                setProcessError(error);
                onDataLoadFailed();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationRefreshId, enabled, setIsInProgress, setResult, onDataLoaded, setProcessError, onDataLoadFailed]);

    return {
        isInProgress,
        processError,
        result,
        reset,
        cursorPagination,
        refresh,
        count,
    };
}

export function usePost(postId: number): UseQueryResult<FetchPostType> {
    return useQuery(['post', postId], () => getPostApi(postId), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        enabled: !Number.isNaN(postId),
    });
}

export function usePostsFilter(): UsePostFilterHookType {
    const {queries: postsFilterQueries, setQuery: setPostQuery} = useUrl<PostsFilterQueriesType>();
    const initialPostsFilter = getInitialPostsFilter(postsFilterQueries);
    const [filter, setFilter] = useState<PostsFilterStateType>(initialPostsFilter);
    const setFilterWrapped = useCallback<SetPostFilterType>(
        (filterPartial: Partial<PostsFilterStateType>) => {
            const resultFilter: PostsFilterStateType = {...filter, ...filterPartial};
            const [dateFrom, dateTo] = resultFilter[PostsFilterKeyEnum.timeRange];

            setFilter(resultFilter);
            const dateFromQueryValue = dateFrom !== null ? getIsoYyyyMmDdString(dateFrom) : '';
            const dateToQueryValue = dateTo !== null ? getIsoYyyyMmDdString(dateTo) : '';

            setPostQuery({
                [PostsFilterStateUrlQueryNameEnum.createdAtAfter]: dateFromQueryValue,
                [PostsFilterStateUrlQueryNameEnum.createdAtBefore]: dateToQueryValue,
                [PostsFilterStateUrlQueryNameEnum.statuses]: resultFilter[PostsFilterKeyEnum.statuses].join(','),
                [PostsFilterStateUrlQueryNameEnum.catalogs]: resultFilter[PostsFilterKeyEnum.sources].join(','),
            });
        },

        [filter, setFilter, setPostQuery]
    );

    return {filter, setFilter: setFilterWrapped};
}

export function usePostsAvailableCatalogs(): PostsAvailableCatalogsHookType {
    const [availableCatalogs, setAvailableCatalogs] = useState<Array<ShortCatalogType>>([]);
    const [processError, setProcessError] = useState<Error | null>(null);
    const [isInProgress, setIsInProgress] = useState<boolean>(false);

    const loadData = useCallback(async () => {
        try {
            setIsInProgress(true);
            const fetchedCatalogs = await getPostAvailableCatalogsApi();

            setAvailableCatalogs(fetchedCatalogs);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setProcessError(error);
            }
        } finally {
            setIsInProgress(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData, setIsInProgress, setProcessError]);

    const catalogsInfo = useMemo(() => {
        return {
            [PostSourceEnum.google]:
                availableCatalogs.find((catalog) => catalog.id === ProvidersIdsEnum.google) || null,
            [PostSourceEnum.yandex]:
                availableCatalogs.find((catalog) => catalog.id === ProvidersIdsEnum.yandex) || null,
            [PostSourceEnum.facebook]:
                availableCatalogs.find((catalog) => catalog.id === ProvidersIdsEnum.facebook) || null,
            [PostSourceEnum.vkontakte]:
                availableCatalogs.find((catalog) => catalog.id === ProvidersIdsEnum.vkontakte) || null,
            [PostSourceEnum.instagram]:
                availableCatalogs.find((catalog) => catalog.id === ProvidersIdsEnum.instagram) || null,
        };
    }, [availableCatalogs]);

    return {
        isInProgress,
        processError,
        availableCatalogs,
        catalogsInfo,
    };
}

export function usePostsCatalogs(): Omit<UseHookType<Array<ShortCatalogType>>, 'reset'> {
    const {isInProgress, setIsInProgress, result, setResult, processError, setProcessError} =
        useApiHooks<Array<ShortCatalogType>>();

    useEffect(() => {
        setIsInProgress(true);
        getPostCatalogsApi()
            .then((response) => setResult(response.results))
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setResult, setProcessError]);

    return {
        isInProgress,
        processError,
        result,
    };
}

export function usePostsCalendar(panel: CalendarPanelType): UseHookType<CalendarPostsResponseType> {
    const {isInProgress, setIsInProgress, result, setResult, processError, setProcessError, reset} =
        useApiHooks<CalendarPostsResponseType>();

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const {user} = useUser();

    useEffect(() => {
        setIsInProgress(true);

        const parameters: CalendarPostsRequestParametersType = {
            year: panel.date.year(),
            month: panel.mode === 'month' ? panel.date.month() + 1 : null,
        };

        getPostsCalendar(parameters, user?.isDemoCpUser ? '' : mainFilterKey)
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setResult, setProcessError, panel, user, mainFilterKey]);

    return {
        isInProgress,
        processError,
        result,
        reset,
    };
}

export function useLastPosts(): LastPostsHookType {
    const {isInProgress, setIsInProgress, result, setResult, processError, setProcessError, reset} =
        useApiHooks<Array<LastPostsType>>();

    const getLastPosts = useCallback(() => {
        setIsInProgress(true);

        const lastPostsPromise = getLastPostsApi();

        lastPostsPromise
            .then((postsResponse) => {
                setResult(postsResponse);
                setProcessError(null);
            })
            .finally(() => {
                setIsInProgress(false);
            })
            .catch((error: Error) => {
                setProcessError(error);
            });

        return lastPostsPromise;
    }, [setIsInProgress, setResult, setProcessError]);

    return {
        getLastPosts,
        isInProgress,
        processError,
        result,
        reset,
    };
}

export function useCatalogPostLinks(postId: number): CatalogLinksHookType {
    const [filter, setFilter] = useState<PostLinksFilterType>({
        statuses: [],
        providers: [],
    });

    const changeFilter = useCallback(
        (newFilter: PostLinksFilterType) => {
            const isStatusesChanged = !isArraysEqual(newFilter.statuses, filter.statuses);
            const isProvidersChanged = !isArraysEqual(newFilter.providers, filter.providers);

            if (isStatusesChanged || isProvidersChanged) {
                setFilter(newFilter);
            }
        },
        [filter.providers, filter.statuses]
    );

    const pagination = usePagination({
        dependencies: filter,
        initialPageSize: 50,
        shouldSaveState: false,
    });

    const {page, pageSize, onDataLoaded} = pagination;

    const queryOptions = useMemo(
        () => ({
            page,
            pageSize,
            postId,
            ...filter,
        }),
        [page, pageSize, postId, filter]
    );

    const query = useQuery(['post-links', postId, queryOptions], () => getPostLinks(queryOptions));

    useEffect(() => {
        if (query.data) {
            onDataLoaded(query.data);
        }
    }, [onDataLoaded, query.data]);

    return {...query, pagination, setFilter: changeFilter};
}

export function usePostSubmitInformation(
    options: PostSubmitInformationParametersType
): UseQueryResult<PostSubmitInformationType> {
    return useQuery<PostSubmitInformationType>(
        ['post-submit-information', options.selectorId, options.providerCodes],
        () => getPostSubmitInformation(options),
        {
            enabled: options.providerCodes.length > 0,
        }
    );
}

const AI_GENERATION_TIMEOUT_MS = 120_000;

function useGenerationTimeout(isGenerating: boolean, onTimeout: () => void): void {
    const onTimeoutRef = useRef<() => void>(onTimeout);

    onTimeoutRef.current = onTimeout;

    useEffect(() => {
        const timeoutId = isGenerating ? setTimeout(() => onTimeoutRef.current(), AI_GENERATION_TIMEOUT_MS) : null;

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isGenerating]);
}

function usePostAiSuggestErrorSnackbars(): {
    showActionError: (rawErrorCode: string, isImageError: boolean) => void;
    showRequestError: (error: unknown) => void;
    showTimeoutError: (isImageError: boolean) => void;
} {
    const {snackbar} = useSnackbar();

    const showActionError = useCallback(
        (rawErrorCode: string, isImageError: boolean) => {
            const errorCode = getEnumValueEnsure(AiGenerationErrorEnum, rawErrorCode, AiGenerationErrorEnum.internal);

            snackbar.error({
                message: (
                    <Locale
                        stringKey={
                            isImageError ? 'POSTS_AI_GENERATION__ERROR__IMAGE_TITLE' : 'POSTS_SUGGESTS_ERROR__TITLE'
                        }
                    />
                ),
                description: <Locale stringKey={aiGenerationErrorTranslationMap[errorCode]} />,
            });
        },
        [snackbar]
    );

    const showRequestError = useCallback(
        (error: unknown) => {
            if (error instanceof ApiError && error.statusCode === HttpCodeEnum.Forbidden) {
                snackbar.error({
                    message: <Locale stringKey="LICENSE_SNACKBAR__NOT_ACTIVE__TITLE" />,
                    description: <Locale stringKey="LICENSE_SNACKBAR__NOT_ACTIVE__MESSAGE" />,
                });
                return;
            }

            snackbar.error({
                message: <Locale stringKey="POSTS_SUGGESTS_ERROR__TITLE" />,
                description: <Locale stringKey="POSTS_SUGGESTS_ERROR__CONTENT" />,
            });
        },
        [snackbar]
    );

    const showTimeoutError = useCallback(
        (isImageError: boolean) => {
            snackbar.error({
                message: (
                    <Locale
                        stringKey={
                            isImageError ? 'POSTS_AI_GENERATION__ERROR__IMAGE_TITLE' : 'POSTS_SUGGESTS_ERROR__TITLE'
                        }
                    />
                ),
                description: <Locale stringKey="POSTS_SUGGESTS_ERROR__CONTENT" />,
            });
        },
        [snackbar]
    );

    return {showActionError, showRequestError, showTimeoutError};
}

function useAiSuggestCentrifugoFeed(options: {
    channelId: string;
    onTextAction: (action: PostAiSuggestTextActionType) => void;
    onImageAction: (action: PostAiSuggestImageActionType) => void;
}): void {
    const {channelId, onTextAction, onImageAction} = options;

    const {getSubscription} = useCentrifugeSubscription();

    const {user} = useUser();

    const processedEventsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        function isAlreadyProcessed(eventKey: string): boolean {
            if (processedEventsRef.current.has(eventKey)) {
                return true;
            }

            processedEventsRef.current.add(eventKey);

            return false;
        }

        function onActionReceived({data}: PublicationContext) {
            const camelcaseData = camelizeKeys(data);

            if (isAiSuggestImageAction(camelcaseData)) {
                if (!isAlreadyProcessed(`image:${camelcaseData.requestId}`)) {
                    onImageAction(camelcaseData);
                }

                return;
            }

            if (isAiSuggestTextAction(camelcaseData) && !isAlreadyProcessed(`text:${camelcaseData.requestId}`)) {
                onTextAction(camelcaseData);
            }
        }

        const subscription = getSubscription(
            getCentrifugePostSuggestChannel({
                userId: String(user?.id),
                channelId,
            })
        );

        subscription?.on('publication', onActionReceived);

        return () => {
            subscription?.removeListener('publication', onActionReceived);
        };
    }, [channelId, getSubscription, onImageAction, onTextAction, user]);
}

// Exported so the carousel hint can state the cap instead of hardcoding the number in every locale.
export const AI_GENERATION_MAX_SLIDES = 3;

// A single slide of the carousel. `textRequestId` correlates the text event; `imageRequestId`
// correlates the (current) image event — it is re-pointed to a fresh request_id on regenerate.
type AiSuggestSlideType = {
    textRequestId: string;
    imageRequestId: string | null;
    suggest: PostAiSuggestItemType | null;
    image: PostAiGeneratedImageType | null;
};

// eslint-disable-next-line max-statements -- cohesive slides/text/image orchestration hook
export function usePostAiSuggests(selectorId: string, formInstance: FormInstance<PostFormType>): PostAiSuggestHookType {
    // slides keyed by request_id; text and image events fill their slot regardless of arrival order
    const [slides, setSlides] = useState<Array<AiSuggestSlideType>>([]);
    const [activeSuggestIndex, setActiveSuggestIndex] = useState<number>(0);
    const [isGeneratingSuggest, setIsGeneratingSuggest] = useState<boolean>(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
    const [generatingImageIndex, setGeneratingImageIndex] = useState<number | null>(null);
    const [lastSettings, setLastSettings] = useState<PostAiGenerationSettingsType | null>(null);

    const lastSettingsRef = useRef<PostAiGenerationSettingsType | null>(null);

    // Correlation must not depend on when React runs a state updater: an updater is only evaluated
    // eagerly while the fiber has no pending update, so a text event arriving in the same tick as an
    // image event used to be dropped (its `isTracked` flag stayed false) and the modal hung on the
    // loading step until the 120s timeout. The ref is written synchronously on every slides change,
    // so incoming events always correlate against the current slides.
    const slidesRef = useRef<Array<AiSuggestSlideType>>([]);

    const updateSlides = useCallback((nextSlides: Array<AiSuggestSlideType>) => {
        slidesRef.current = nextSlides;
        setSlides(nextSlides);
    }, []);

    const channelId = useMemo(() => uuidv4(), []);

    const {showActionError, showRequestError, showTimeoutError} = usePostAiSuggestErrorSnackbars();

    useGenerationTimeout(isGeneratingSuggest, () => {
        setIsGeneratingSuggest(false);
        showTimeoutError(false);
    });

    useGenerationTimeout(isGeneratingImage, () => {
        setIsGeneratingImage(false);
        setGeneratingImageIndex(null);
        showTimeoutError(true);
    });

    const handleTextAction = useCallback(
        (action: PostAiSuggestTextActionType) => {
            const previousSlides = slidesRef.current;
            const targetIndex = previousSlides.findIndex((slide) => slide.textRequestId === action.requestId);

            // a text event for one of our slides only: ignore other consumers on this channel
            if (targetIndex === -1) {
                return;
            }

            // text is in: the modal can leave the loading step and show it while the image keeps loading
            setIsGeneratingSuggest(false);

            if (action.error) {
                showActionError(action.error, false);

                return;
            }

            updateSlides(
                previousSlides.map((slide, index) =>
                    index === targetIndex ? {...slide, suggest: parseTextActionSuggest(action)} : slide
                )
            );
        },
        [showActionError, updateSlides]
    );

    const handleImageAction = useCallback(
        (action: PostAiSuggestImageActionType) => {
            const previousSlides = slidesRef.current;
            const targetIndex = previousSlides.findIndex((slide) => slide.imageRequestId === action.requestId);

            // an image event for one of our slides only
            if (targetIndex === -1) {
                return;
            }

            const image = action.images?.[0] ?? null;

            // on error keep the existing image, only clear the in-flight correlation
            updateSlides(
                previousSlides.map((slide, index) =>
                    index === targetIndex ? {...slide, imageRequestId: null, ...(action.error ? {} : {image})} : slide
                )
            );

            setIsGeneratingImage(false);
            setGeneratingImageIndex((current) => (current === targetIndex ? null : current));

            if (action.error) {
                showActionError(action.error, true);
            }
        },
        [showActionError, updateSlides]
    );

    useAiSuggestCentrifugoFeed({channelId, onTextAction: handleTextAction, onImageAction: handleImageAction});

    const getRequestCatalogs = useCallback((): Array<string> => {
        const posts = formInstance.getFieldValue(PostFormFieldsEnum.Posts) ?? [];

        return posts.map((post) => String(post[SourcePostFormFieldsEnum.Catalog])).filter(Boolean);
    }, [formInstance]);

    const handleSuggestRequestError = useCallback(
        (error: unknown) => {
            setIsGeneratingSuggest(false);
            setIsGeneratingImage(false);
            setGeneratingImageIndex(null);
            showRequestError(error);
        },
        [showRequestError]
    );

    const handleImageRequestError = useCallback(
        (error: unknown) => {
            setIsGeneratingImage(false);
            setGeneratingImageIndex(null);
            showRequestError(error);
        },
        [showRequestError]
    );

    const requestSuggest = useCallback(
        (settings: Omit<PostAiGenerationSettingsType, 'catalogs'>) => {
            const fullSettings: PostAiGenerationSettingsType = {...settings, catalogs: getRequestCatalogs()};
            const requestId = uuidv4();

            lastSettingsRef.current = fullSettings;
            setLastSettings(fullSettings);

            // fresh generation: reset the carousel to a single pending slide. Correlation is in place
            // before the request leaves, so an event can never arrive ahead of its slide.
            updateSlides([
                {
                    textRequestId: requestId,
                    imageRequestId: fullSettings.generateImage ? requestId : null,
                    suggest: null,
                    image: null,
                },
            ]);
            setActiveSuggestIndex(0);
            setIsGeneratingSuggest(true);

            if (fullSettings.generateImage) {
                setIsGeneratingImage(true);
                setGeneratingImageIndex(0);
            }

            requestPostSuggest({selectorId, channelId, requestId, ...fullSettings}).catch(handleSuggestRequestError);
        },
        [channelId, getRequestCatalogs, handleSuggestRequestError, selectorId, updateSlides]
    );

    // "Сгенерировать ещё идеи": appends a brand-new pending slide (text + image if enabled), FIFO max 3
    const requestMoreSuggests = useCallback(() => {
        const settings = lastSettingsRef.current;

        if (!settings) {
            return;
        }

        const requestId = uuidv4();
        const fullSettings: PostAiGenerationSettingsType = {...settings, catalogs: getRequestCatalogs()};

        const nextSlides = [
            ...slidesRef.current,
            {
                textRequestId: requestId,
                imageRequestId: fullSettings.generateImage ? requestId : null,
                suggest: null,
                image: null,
            },
        ].slice(-AI_GENERATION_MAX_SLIDES);

        const newIndex = nextSlides.length - 1;

        updateSlides(nextSlides);
        setActiveSuggestIndex(newIndex);
        setIsGeneratingSuggest(true);

        if (fullSettings.generateImage) {
            setIsGeneratingImage(true);
            // Point the image loader at the freshly appended slide, otherwise its spinner stays
            // hidden until the image event arrives (it keys off generatingImageIndex === active).
            setGeneratingImageIndex(newIndex);
        }

        requestPostSuggest({selectorId, channelId, requestId, ...fullSettings}).catch(handleSuggestRequestError);
    }, [channelId, getRequestCatalogs, handleSuggestRequestError, selectorId, updateSlides]);

    const regenerateImage = useCallback(
        (index: number) => {
            const settings = lastSettingsRef.current;

            if (!settings) {
                return;
            }

            const requestId = uuidv4();

            // re-point this slide's image correlation to the new request, leaving siblings untouched
            updateSlides(
                slidesRef.current.map((slide, index_) =>
                    index_ === index ? {...slide, imageRequestId: requestId, image: null} : slide
                )
            );
            setIsGeneratingImage(true);
            setGeneratingImageIndex(index);

            requestPostImageSuggest({
                selectorId,
                channelId,
                requestId,
                imagePrompt: settings.userPrompt ?? '',
                imageSize: settings.imageSize ?? AiImageSizeEnum.square,
                imageCount: 1,
            }).catch(handleImageRequestError);
        },
        [channelId, handleImageRequestError, selectorId, updateSlides]
    );

    const deleteImage = useCallback(
        (index: number) => {
            updateSlides(
                slidesRef.current.map((slide, index_) =>
                    index_ === index ? {...slide, imageRequestId: null, image: null} : slide
                )
            );
        },
        [updateSlides]
    );

    const editText = useCallback(
        (index: number, text: string) => {
            updateSlides(
                slidesRef.current.map((slide, index_) =>
                    index_ === index && slide.suggest ? {...slide, suggest: {...slide.suggest, text}} : slide
                )
            );
        },
        [updateSlides]
    );

    // The carousel UX consumes parallel suggests/images arrays kept index-aligned with `slides`,
    // so activeSuggestIndex / generatingImageIndex stay valid across both. Slides awaiting their text
    // event render as empty text until it arrives (the modal stays on the Loading step meanwhile).
    const suggests = useMemo(
        () => slides.map((slide): PostAiSuggestItemType => slide.suggest ?? {text: '', cta: null}),
        [slides]
    );

    const images = useMemo(() => slides.map((slide) => slide.image), [slides]);

    return {
        suggests,
        images,
        activeSuggestIndex,
        setActiveSuggestIndex,
        isGeneratingSuggest,
        isGeneratingImage,
        generatingImageIndex,
        requestSuggest,
        requestMoreSuggests,
        regenerateImage,
        deleteImage,
        editText,
        lastSettings,
    };
}

// Legacy AI text-suggestion flow (ai_post_generation_v2 OFF): restores the pre-AI-22 behaviour — a flat,
// append-only suggestions list (no slides / images / CTA). Feeds LegacyPostAiSuggest through the shared
// PostAiSuggestHookType; the v2-only fields are inert stubs. Remove together with the rollout flag.
export function useLegacyPostAiSuggests(selectorId: string): PostAiSuggestHookType {
    const [suggests, setSuggests] = useState<Array<PostAiSuggestItemType>>([]);
    const [isGeneratingSuggest, setIsGeneratingSuggest] = useState<boolean>(false);

    const {getSubscription} = useCentrifugeSubscription();
    const {user} = useUser();
    const {showRequestError, showTimeoutError} = usePostAiSuggestErrorSnackbars();

    const channelId = useMemo(() => uuidv4(), []);

    useGenerationTimeout(isGeneratingSuggest, () => {
        setIsGeneratingSuggest(false);
        showTimeoutError(false);
    });

    const handleSuggestRequestError = useCallback(
        (error: unknown) => {
            setIsGeneratingSuggest(false);
            showRequestError(error);
        },
        [showRequestError]
    );

    useEffect(() => {
        function onActionReceived({data}: PublicationContext) {
            const camelcaseData = camelizeKeys(data);

            // legacy event has no `type`, only postSuggestions — append all of them (prior carousel behaviour)
            if (isAiSuggestLegacyAction(camelcaseData)) {
                setIsGeneratingSuggest(false);
                setSuggests((previousSuggests) => [
                    ...previousSuggests,
                    ...camelcaseData.postSuggestions.map((text) => ({text, cta: null})),
                ]);
            }
        }

        const subscription = getSubscription(getCentrifugePostSuggestChannel({userId: String(user?.id), channelId}));

        subscription?.on('publication', onActionReceived);

        return () => {
            subscription?.removeListener('publication', onActionReceived);
        };
    }, [channelId, getSubscription, user]);

    const requestSuggest = useCallback(() => {
        setIsGeneratingSuggest(true);
        requestPostSuggest({selectorId, channelId, requestId: uuidv4()}).catch(handleSuggestRequestError);
    }, [channelId, handleSuggestRequestError, selectorId]);

    return {
        suggests,
        isGeneratingSuggest,
        requestSuggest,
        // v2-only fields, inert in the legacy flow
        images: [],
        activeSuggestIndex: 0,
        setActiveSuggestIndex: noop,
        isGeneratingImage: false,
        generatingImageIndex: null,
        requestMoreSuggests: noop,
        regenerateImage: noop,
        deleteImage: noop,
        editText: noop,
        lastSettings: null,
    };
}

export function usePostGenerationOptions(options: {enabled: boolean}): UseQueryResult<PostGenerationOptionsType> {
    return useQuery(['post-generation-options'], getPostGenerationOptions, {
        enabled: options.enabled,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
    });
}

export function usePostsFacebookPages(): UseQueryResult<Array<PostFbPageType>> {
    return useQuery(['posts-facebook-pages'], fetchAvailableFbPages);
}

export function usePostsVkGroups(): UseQueryResult<Array<PostVkGroupType>> {
    return useQuery(['posts-vk-groups'], fetchAvailableVkGroups);
}

export function usePostsInstagramPages(): UseQueryResult<Array<PostInstagramPageType>> {
    return useQuery(['posts-instagram-pages'], fetchAvailableInstagramPages);
}

export function useCreatePostMutation(status: PostStatusEnum): UseMutationResult<IdNumberType, unknown, PostFormType> {
    return useMutation({
        mutationFn: (body: PostFormType) => createPostApi(body, status),
    });
}

export function useUpdatePostMutation(status: PostStatusEnum): UseMutationResult<void, unknown, PostFormType> {
    return useMutation({
        mutationFn: (body: PostFormType) => updatePostApi(body, status),
    });
}

export function useRemovePostMutation(): UseMutationResult<void, unknown, number> {
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: (postId: number) => removePostApi(postId),
        onSuccess: () => {
            snackbar.success(<Locale stringKey="POSTS_FORM__MESSAGE__REMOVE_POST_SUCCESS" />);
        },
        onError: () => {
            snackbar.error(<Locale stringKey="POSTS_FORM__MESSAGE__REMOVE_POST_ERROR" />);
        },
    });
}

export function useHardRemovePostMutation(): UseMutationResult<void, unknown, number> {
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: (postId: number) => hardRemovePostApi(postId),
        onSuccess: () => {
            snackbar.success(<Locale stringKey="POSTS_FORM__MESSAGE__HARD_REMOVE__POST_SUCCESS" />);
        },
        onError: () => {
            snackbar.error(<Locale stringKey="POSTS_FORM__MESSAGE__HARD_REMOVE__POST_ERROR" />);
        },
    });
}

export function usePostFormSubmitErrors(
    formInstance: FormInstance<PostFormType>,
    currentStep: PostStepEnum
): {
    stepsWithErrors: Array<PostStepEnum>;
    handleSubmitError: (error: unknown) => void;
} {
    const [errors, setErrors] = useState<Array<FormErrorType>>([]);

    const stepsWithErrors = useMemo(
        () =>
            Object.values(PostStepEnum).filter((postStep) =>
                errors.some((error) => {
                    const errorFieldName = getEnumValue(PostFormFieldsEnum, String(error?.name));

                    return errorFieldName && FORM_STEP_FIELDS[postStep].includes(errorFieldName);
                })
            ),
        [errors]
    );

    const setCurrentStepErrors = useCallback(() => {
        setErrors((previousErrors) => {
            formInstance.setFields(previousErrors);
            return previousErrors.filter((error) => {
                const errorFieldName = getEnumValue(PostFormFieldsEnum, String(error?.name[0]));

                return errorFieldName && FORM_STEP_FIELDS[currentStep].includes(errorFieldName);
            });
        });
    }, [currentStep, formInstance]);

    useEffect(() => {
        setCurrentStepErrors();
    }, [setCurrentStepErrors]);

    const handleSubmitError = useCallback((error: unknown) => {
        if (error instanceof ApiError) {
            setErrors(getFormErrors(error.jsonData));
        }
    }, []);

    return {
        stepsWithErrors,
        handleSubmitError,
    };
}

export function useCatalogPreviewData(
    catalog: PostSourceEnum,
    options: PostPreviewOptionsType
): UseQueryResult<PostPreviewDetailsType> {
    return useQuery(['company-preview-data', catalog], () => fetchCompanyPreviewData(catalog, options), {
        retry: 0,
    });
}

export function usePostRefresh(): UseMutationResult<unknown, unknown, number> {
    return useMutation(['post-refresh'], (postId) => refreshPost(postId));
}
